import dayjs from "dayjs";
import jwt_decode from "jwt-decode";
import { IIdentityTokenResponse, ITokenResponse } from "store/user-session/user-session-model";
import Cookies from "universal-cookie";
import { readConfigFile } from "utils/appConfig";
import { saveKeycloakTokenForNps } from "utils/auth/localStorage";
import Request from "utils/requests/api";
import initializeConfig from "utils/requests/api.config";
import NavService from "./Nav.service";

class TokenService {
  public saveToken(token: string) {
    const cookies = new Cookies();
    cookies.set("token", token, { path: "/" });
    cookies.remove("token_first_part", { path: "/" });
    cookies.remove("token_second_part", { path: "/" });
    return Promise.resolve();
  }

  public deleteToken() {
    const cookies = new Cookies();
    cookies.remove("token", { path: "/" });
    cookies.remove("token_first_part", { path: "/" });
    cookies.remove("token_second_part", { path: "/" });
  }

  public async saveIdentityTokenResponse(response: IIdentityTokenResponse) {
    await this.saveToken(response.token);
    saveKeycloakTokenForNps(response.kcToken);
  }

  public getToken() {
    const cookies = new Cookies();
    return cookies.get("token") || "";
  }

  public decodeToken() {
    const token = this.getToken();
    if (!token) {
      return null;
    }

    try {
      const tokenDecoded: ITokenResponse = jwt_decode(token);

      return tokenDecoded;
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  }

  public async validRefreshToken() {
    const tokenDecoded = this.decodeToken();
    if (tokenDecoded !== null) {
      const { exp } = tokenDecoded;
      const expireDate = dayjs(new Date(exp * 1000));
      const now = dayjs();
      const duration = dayjs.duration(expireDate.diff(now));
      const minutes = duration.minutes();
      if (minutes <= 5 && minutes >= 0) {
        const identityTokenResponse = await this.refreshAccessToken();
        await this.saveIdentityTokenResponse(identityTokenResponse);
      }
    }
  }

  public async userInfoTokenDecoded() {
    const tokenDecoded = this.decodeToken();
    return {
      username: tokenDecoded?.name || "",
      email: tokenDecoded?.email || "",
      locationId: +tokenDecoded?.locationId || 0,
      location: tokenDecoded?.locationDescription || "",
      role: tokenDecoded?.role || "",
      warehouseCode: tokenDecoded?.warehouseCode || "",
      acceptLanguage: tokenDecoded?.acceptLanguage || "",
    };
  }

  public async refreshToken() {
    const tokenDecoded = this.decodeToken();
    if (tokenDecoded !== null) {
      const identityTokenResponse = await this.refreshAccessToken();
      await this.saveIdentityTokenResponse(identityTokenResponse);
    }
  }

  public async updateRefreshToken() {
    const identityTokenResponse = await this.refreshAccessToken();
    await this.saveIdentityTokenResponse(identityTokenResponse);
    const userInfo = this.userInfoTokenDecoded();
    return userInfo;
  }

  public async refreshDecodeToken() {
    const userInfo = this.userInfoTokenDecoded();
    return userInfo;
  }

  public async refreshAccessToken(): Promise<IIdentityTokenResponse> {
    try {
      const config = await readConfigFile();
      const isApim = config.VITE_YMS_APIM_URL ? "/identity/v1" : "/identity";
      const authenticationRoute = `${isApim}/authentications:refresh`;
      const identityRoute = config.VITE_YMS_APIM_URL ? `${config.VITE_YMS_APIM_URL}${authenticationRoute}` : `${config.VITE_YMS_API_URL}${authenticationRoute}`;
      const response = await (await Request).get(identityRoute);
      console.info("TOKEN UPDATED AT " + new Date().toISOString());
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  public checkAuthToken(token: string): Promise<any> {
    if (!token) {
      return Promise.reject(false);
    }
    return Promise.resolve(true);
  }

  public async authenticateToken() {
    const navService = new NavService();
    const config = await initializeConfig();
    try {
      const token = this.getToken();
      const response = await this.checkAuthToken(token);
      if (!response) {
        this.deleteToken();
        navService.redirectUser(config.WebsiteUrl);
      }
    } catch (e) {
      this.deleteToken();
      navService.redirectUser(config.WebsiteUrl);
    }
  }

  public async getRoleToken() {
    const tokenDecoded = this.decodeToken();
    if (tokenDecoded) {
      const { role } = tokenDecoded;
      return role;
    }
  }
}

export default TokenService;
